import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Copy, Reactivate } from "@dreamhub/designsystem/icons";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import Banner from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import {
  useRotateWebFormKey,
  useSaveWebFormForm,
  useSaveWebFormStatus,
  useWebFormScriptTag,
  useWebFormSettings,
  webFormErrorDetail
} from "@/entities/webForm/hooks";
import { WebFormFieldMap } from "@/pages/settings/components/integrations/webForm/WebFormFieldMap";
import { WebFormRouting } from "@/pages/settings/components/integrations/webForm/WebFormRouting";
import { parseOrigins } from "@/pages/settings/components/integrations/webForm/webFormOptions";
import Section, {
  SETTINGS_CARD_CONTAINER_CLASS
} from "@/pages/settings/components/sections/Section";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";
import { cn } from "@/utils/utils";

const PREFIX = "integrations.webFormSettings";
const MAX_SELECTOR_LENGTH = 500;
const FORM_ERROR_ID = "web-form-settings-form-error";

const sameList = (first: string[], second: string[]) =>
  first.length === second.length &&
  first.every((value, index) => value === second[index]);

const FIELD_CLASSNAME =
  "w-[240px] rounded-lg bg-surface-field-default shadow-field outline-none";

const WebFormControls: React.FC = () => {
  const { t } = useTranslation();
  const settingsQuery = useWebFormSettings();
  const settings = settingsQuery.data;
  const canShowTag = Boolean(settings?.hasKey && settings.formSelector);
  const tagQuery = useWebFormScriptTag(canShowTag);
  const saveStatus = useSaveWebFormStatus();
  const saveForm = useSaveWebFormForm();
  const rotateKey = useRotateWebFormKey();
  const [isRotateOpen, setIsRotateOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [draftSelector, setDraftSelector] = useState(
    settings?.formSelector ?? ""
  );
  const [draftOrigins, setDraftOrigins] = useState(
    (settings?.allowedOrigins ?? []).join("\n")
  );
  // Keyed on the saved form alone, so turning the form on or off keeps a draft.
  const savedForm = settings
    ? JSON.stringify([settings.formSelector, settings.allowedOrigins])
    : null;
  const [syncedForm, setSyncedForm] = useState(savedForm);
  // Show what was saved once it loads or changes, replacing any older draft.
  if (savedForm !== syncedForm) {
    setSyncedForm(savedForm);
    if (settings) {
      setDraftSelector(settings.formSelector);
      setDraftOrigins(settings.allowedOrigins.join("\n"));
    }
  }

  const isBusy =
    saveStatus.isPending || saveForm.isPending || rotateKey.isPending;
  const controlsDisabled = !settings || isBusy;
  const origins = parseOrigins(draftOrigins);
  const selector = draftSelector.trim();
  const isDirty =
    !!settings &&
    (selector !== settings.formSelector ||
      !sameList(origins, settings.allowedOrigins));
  const canSaveForm = isDirty && selector !== "" && !isBusy;

  const sectionSubtitle = (key: string) =>
    settingsQuery.isError ? t(`${PREFIX}.loadError`) : t(key);

  const handleSaveForm = () => {
    setFormError(null);
    saveForm.mutate(
      { data: { formSelector: selector, allowedOrigins: origins } },
      {
        onSuccess: () => toast.success(t(`${PREFIX}.form.saved`)),
        onError: (error) => {
          const message =
            webFormErrorDetail(error) ?? t(`${PREFIX}.form.saveError`);
          setFormError(message);
          toast.error(message);
        }
      }
    );
  };

  const handleCopy = async () => {
    if (!tagQuery.data) return;
    try {
      await navigator.clipboard.writeText(tagQuery.data);
      toast.success(t(`${PREFIX}.tag.copied`));
    } catch {
      // Clipboard API unavailable in non-secure contexts.
      toast.error(t(`${PREFIX}.tag.copyError`));
    }
  };

  const handleRotate = () => {
    rotateKey.mutate(
      {},
      {
        onSuccess: () => {
          setIsRotateOpen(false);
          toast.success(t(`${PREFIX}.rotate.done`));
        },
        onError: () => {
          setIsRotateOpen(false);
          toast.error(t(`${PREFIX}.rotate.error`));
        }
      }
    );
  };

  // Rotation is how a leaked key is replaced, so it stays reachable even when
  // the tag itself can't be loaded.
  const rotateButton = (
    <IconButton
      size="md"
      onClick={() => setIsRotateOpen(true)}
      disabled={controlsDisabled}
      aria-label={t(`${PREFIX}.rotate.action`)}
      tooltipText={t(`${PREFIX}.rotate.action`)}
      data-test="web-form-settings-rotate"
    >
      <Reactivate />
    </IconButton>
  );

  const renderTag = () => {
    if (!canShowTag) {
      return (
        <Text variant="bodySecondary" data-test="web-form-settings-tag-hint">
          {t(`${PREFIX}.tag.notReady`)}
        </Text>
      );
    }
    if (tagQuery.isError) {
      return (
        <div className="flex items-center gap-2">
          <Text
            variant="smBodyError"
            className="flex-1"
            data-test="web-form-settings-tag-error"
          >
            {t(`${PREFIX}.tag.loadError`)}
          </Text>
          {rotateButton}
        </div>
      );
    }
    return (
      <>
        <div className="flex items-center gap-2">
          <Text variant="bodySecondary" className="flex-1">
            {t(`${PREFIX}.tag.instructions`)}
          </Text>
          {rotateButton}
          <Button
            variant="secondary"
            className="h-7 px-1.5"
            onClick={handleCopy}
            disabled={!tagQuery.data}
            data-test="web-form-settings-copy"
          >
            <Copy className="h-4 w-4" />
            {t(`${PREFIX}.tag.copy`)}
          </Button>
        </div>
        <pre
          className="max-h-[280px] w-full overflow-auto whitespace-pre-wrap break-all rounded-lg border border-border-border1 bg-surface-field-default p-2 font-mono text-sm text-secondary"
          data-test="web-form-settings-tag"
        >
          {tagQuery.data ?? ""}
        </pre>
      </>
    );
  };

  return (
    <div className="flex w-full flex-col gap-12" data-test="web-form-settings">
      {settings && !settings.settingsValid && (
        <div
          className="flex w-full flex-col gap-2"
          data-test="web-form-settings-invalid"
        >
          <Banner
            variant="warning"
            title={t(`${PREFIX}.invalid.title`)}
            showCloseButton={false}
          />
          <Text variant="bodySecondary" className="px-1">
            {t(`${PREFIX}.invalid.body`)}
          </Text>
        </div>
      )}
      <Section
        isLoading={settingsQuery.isLoading}
        items={[
          {
            title: t(`${PREFIX}.status.enabled.title`),
            subTitle: t(`${PREFIX}.status.enabled.subtitle`),
            customAction: (
              <Switch
                data-test="web-form-settings-enabled"
                aria-label={t(`${PREFIX}.status.enabled.title`)}
                checked={settings?.enabled ?? false}
                disabled={controlsDisabled}
                onCheckedChange={(enabled) =>
                  saveStatus.mutate(
                    { data: { enabled } },
                    {
                      onError: () => toast.error(t(`${PREFIX}.status.error`))
                    }
                  )
                }
              />
            )
          }
        ]}
      />
      <Section
        title={t(`${PREFIX}.tag.title`)}
        subTitle={
          settingsQuery.isError ? t(`${PREFIX}.loadError`) : undefined
        }
        isLoading={settingsQuery.isLoading}
      >
        <div
          className={cn(SETTINGS_CARD_CONTAINER_CLASS, "gap-2 p-4")}
          data-test="web-form-settings-tag-card"
        >
          {renderTag()}
        </div>
      </Section>
      <Section
        title={t(`${PREFIX}.form.title`)}
        subTitle={sectionSubtitle(`${PREFIX}.form.subtitle`)}
        isLoading={settingsQuery.isLoading}
        items={[
          {
            title: t(`${PREFIX}.form.selector.title`),
            subTitle: t(`${PREFIX}.form.selector.subtitle`),
            customAction: (
              <Input
                data-test="web-form-settings-selector"
                aria-label={t(`${PREFIX}.form.selector.title`)}
                placeholder={t(`${PREFIX}.form.selector.placeholder`)}
                value={draftSelector}
                onChange={(event) => {
                  setDraftSelector(event.target.value);
                  setFormError(null);
                }}
                aria-describedby={formError ? FORM_ERROR_ID : undefined}
                maxLength={MAX_SELECTOR_LENGTH}
                disabled={controlsDisabled}
                containerClassName="w-[240px]"
              />
            )
          },
          {
            title: t(`${PREFIX}.form.origins.title`),
            subTitle: t(`${PREFIX}.form.origins.subtitle`),
            customAction: (
              <Textarea
                data-test="web-form-settings-origins"
                aria-label={t(`${PREFIX}.form.origins.title`)}
                placeholder={t(`${PREFIX}.form.origins.placeholder`)}
                value={draftOrigins}
                onChange={(event) => {
                  setDraftOrigins(event.target.value);
                  setFormError(null);
                }}
                aria-describedby={formError ? FORM_ERROR_ID : undefined}
                rows={4}
                disabled={controlsDisabled}
                className={FIELD_CLASSNAME}
              />
            )
          }
        ]}
      >
        {formError && (
          <Text
            variant="smBodyError"
            id={FORM_ERROR_ID}
            role="alert"
            data-test="web-form-settings-form-error"
          >
            {formError}
          </Text>
        )}
        <div className="flex items-center justify-between gap-4">
          <Text variant="bodySecondary">{t(`${PREFIX}.form.originsNote`)}</Text>
          <Button
            onClick={handleSaveForm}
            disabled={!canSaveForm}
            data-test="web-form-settings-save-form"
          >
            {t(`${PREFIX}.form.save`)}
          </Button>
        </div>
      </Section>
      <WebFormRouting
        settings={settings}
        isLoading={settingsQuery.isLoading}
        subTitle={sectionSubtitle(`${PREFIX}.routing.subtitle`)}
        isBusy={isBusy}
      />
      <WebFormFieldMap
        settings={settings}
        isLoading={settingsQuery.isLoading}
        subTitle={sectionSubtitle(`${PREFIX}.fieldMap.subtitle`)}
        isBusy={isBusy}
      />
      <ConfirmationModal
        data-test="web-form-settings-rotate-modal"
        isOpen={isRotateOpen}
        setIsOpen={setIsRotateOpen}
        title={t(`${PREFIX}.rotate.title`)}
        confirmationText={t(`${PREFIX}.rotate.text`)}
        submitButtonText={t(`${PREFIX}.rotate.confirm`)}
        handleSubmit={() => {
          if (!rotateKey.isPending) handleRotate();
        }}
      />
    </div>
  );
};

export const WebFormSettings: React.FC = () => {
  const { t } = useTranslation();
  const isSettingsAdmin = useCanAccessAdminSettings();

  // Every website form route is admin-only, so nobody else gets controls or
  // makes a request that would be refused.
  if (!isSettingsAdmin) {
    return (
      <Text variant="bodySecondary" data-test="web-form-settings-admin-only">
        {t(`${PREFIX}.adminOnly`)}
      </Text>
    );
  }
  return <WebFormControls />;
};
